import React, { useState, useEffect } from 'react';
import { appVersionAPI } from '../services/api';
import './UpdatePrompt.css';

// Read actual installed Android version passed via ?appBuild= URL param by LauncherActivity.
// Falls back to the hardcoded bundle version so web-only users still get a prompt.
const urlBuild = parseInt(new URLSearchParams(window.location.search).get('appBuild') || '0', 10);
const CURRENT_APP_VERSION = urlBuild || 19;
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.techscriptaid.voicemeetpro';

const UpdatePrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    checkForUpdate();
  }, []);

  const checkForUpdate = async () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;

    if (!isStandalone) return;

    try {
      const { data } = await appVersionAPI.getLatest();

      if (CURRENT_APP_VERSION >= data.latestVersion) return;

      // If user already tapped "Update Now" for this version within 24h,
      // suppress the prompt — prevents the bounce-back loop on return from Play Store.
      const attemptedVersion = localStorage.getItem('update_attempted_version');
      const attemptedTime = localStorage.getItem('update_attempted_time');
      if (attemptedVersion === String(data.latestVersion) && attemptedTime) {
        const twentyFourHours = 24 * 60 * 60 * 1000;
        if (Date.now() - parseInt(attemptedTime) < twentyFourHours) return;
      }

      // If user dismissed this exact version less than 3 days ago, don't show
      const dismissedVersion = localStorage.getItem('update_dismissed_version');
      const dismissedTime = localStorage.getItem('update_dismissed_time');
      if (dismissedVersion === String(data.latestVersion) && dismissedTime) {
        const threeDays = 3 * 24 * 60 * 60 * 1000;
        if (Date.now() - parseInt(dismissedTime) < threeDays) return;
      }

      setVersionInfo(data);
      setShowPrompt(true);
    } catch (err) {
      // Silently fail — don't block app usage
    }
  };

  const handleUpdate = () => {
    // Record that user tapped "Update Now" — suppresses prompt on bounce-back
    // from Play Store before the new web bundle has had a chance to load.
    if (versionInfo) {
      localStorage.setItem('update_attempted_version', String(versionInfo.latestVersion));
      localStorage.setItem('update_attempted_time', String(Date.now()));
    }
    setShowPrompt(false);

    const isAndroid = /android/i.test(navigator.userAgent);
    if (isAndroid) {
      // market:// triggers the Play Store app directly via Android intent system.
      // window.open() causes a bounce-back loop in TWA because Chrome intercepts
      // the HTTPS Play Store URL and redirects back into the WebView.
      window.location.href = 'market://details?id=com.techscriptaid.voicemeetpro';
    } else {
      window.open(versionInfo?.playStoreUrl || PLAY_STORE_URL, '_blank');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    if (versionInfo) {
      localStorage.setItem('update_dismissed_version', String(versionInfo.latestVersion));
      localStorage.setItem('update_dismissed_time', String(Date.now()));
    }
  };

  if (!showPrompt || !versionInfo) return null;

  return (
    <div className="update-overlay">
      <div className="update-prompt">
        <div className="update-glow-line" />
        <div className="update-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="updateGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00e5ff" />
                <stop offset="50%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#ff0080" />
              </linearGradient>
            </defs>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="url(#updateGrad)"/>
            <path d="M16 11l-4 4-4-4" stroke="url(#updateGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 7v8" stroke="url(#updateGrad)" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <h3 className="update-title">Update Available</h3>
        <p className="update-version">
          Version {versionInfo.latestVersion} is ready
        </p>
        {versionInfo.releaseNotes && (
          <p className="update-notes">{versionInfo.releaseNotes}</p>
        )}
        <div className="update-actions">
          <button className="update-btn-primary" onClick={handleUpdate}>
            Update Now
          </button>
          <button className="update-btn-secondary" onClick={handleDismiss}>
            Later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePrompt;
